import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Paper,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { VolumeUp, Error, Refresh } from '@mui/icons-material';
import { AudioPreloadProgress } from '../../services/audioPreloader';

interface AudioLoadingOverlayProps {
  progress: AudioPreloadProgress;
  onRetry?: () => void;
  onSkip?: () => void;
}

const AudioLoadingOverlay: React.FC<AudioLoadingOverlayProps> = ({
  progress,
  onRetry,
  onSkip
}) => {
  // Defensive null checks
  if (!progress) {
    return null;
  }

  const { isLoading, loaded, total, currentFile, error } = progress;

  // Don't render anything if not loading and no error
  const shouldShow = isLoading || error || (loaded > 0 && loaded < total);

  if (!shouldShow) {
    return null;
  }

  const progressPercentage = total > 0 ? (loaded / total) * 100 : 0;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        backdropFilter: 'blur(4px)'
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '90%',
          textAlign: 'center',
          borderRadius: 2
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <VolumeUp 
            sx={{ 
              fontSize: 48, 
              color: error ? 'error.main' : 'primary.main',
              mb: 1 
            }} 
          />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            {error ? 'Audio Loading Failed' : 'Loading Audio Files'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error 
              ? 'Some audio files could not be loaded'
              : 'Please wait while we prepare your test audio...'
            }
          </Typography>
        </Box>

        {/* Error State */}
        {error && (
          <Alert 
            severity="warning" 
            icon={<Error />}
            sx={{ mb: 3, textAlign: 'left' }}
          >
            <Typography variant="body2">
              {error}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              You can retry loading or continue with the test. Some audio may not be available.
            </Typography>
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && !error && (
          <>
            <Box sx={{ mb: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={progressPercentage}
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  mb: 1
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {loaded} of {total} files loaded ({Math.round(progressPercentage)}%)
              </Typography>
            </Box>

            {currentFile && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Loading: {currentFile}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">
                Preparing audio files...
              </Typography>
            </Box>
          </>
        )}

        {/* Success State (briefly shown) */}
        {!isLoading && !error && loaded > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" color="success.main" sx={{ fontWeight: 600 }}>
              ✅ All audio files loaded successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Starting your test...
            </Typography>
          </Box>
        )}

        {/* Action Buttons */}
        {error && (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
            {onRetry && (
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={onRetry}
                color="primary"
              >
                Retry Loading
              </Button>
            )}
            {onSkip && (
              <Button
                variant="outlined"
                onClick={onSkip}
                color="inherit"
              >
                Continue Without Audio
              </Button>
            )}
          </Box>
        )}

        {/* Loading indicator for no audio files */}
        {total === 0 && isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              Checking for audio files...
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default React.memo(AudioLoadingOverlay);
